import React from "react";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { addDays } from "@/components/dashboard/planning/planningConstants";

export const PlanningPrintDialog = ({ open, setOpen, printMonth, setPrintMonth }) => {
  const { t, lang } = useLang();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="planning-print-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Printer className="h-4 w-4" />{t("print_planning")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{t("print_planning_desc")}</p>
        <div className="space-y-1">
          <Label className="text-xs">{t("month")}</Label>
          <Input type="month" value={printMonth} onChange={(e) => setPrintMonth(e.target.value)} data-testid="planning-print-month" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button data-testid="planning-print-confirm" disabled={!printMonth}
            onClick={() => { setOpen(false); setTimeout(() => window.open(`/print-planning?month=${printMonth}&lang=${lang}`, "_blank"), 150); }}>
            <Printer className="h-4 w-4 mr-1" />{t("print")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PlanningSplitDialog = ({ split, setSplit, data, splitCandidates, doSplit, busy }) => {
  const { t } = useLang();
  return (
    <Dialog open={!!split} onOpenChange={(o) => !o && setSplit(null)}>
      <DialogContent className="sm:max-w-md" data-testid="planning-split-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("move")} — {split?.bar.guest_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{t("split_move_desc")}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("move_date")}</Label>
            <Input type="date" value={split?.date || ""} min={split?.bar.arrival}
              max={split && addDays(split.bar.departure, -1)}
              onChange={(e) => setSplit({ ...split, date: e.target.value, pitch: "" })} data-testid="split-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("to_pitch")}</Label>
            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={split?.pitch || ""} onChange={(e) => setSplit({ ...split, pitch: e.target.value })} data-testid="split-pitch">
              <option value="">—</option>
              {split && <option value={data.rows[split.bar.row].label}>{data.rows[split.bar.row].label} — {t("same_pitch")}</option>}
              {splitCandidates.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {split && (
          <p className="text-[11px] text-muted-foreground" data-testid="split-preview">
            {data.rows[split.bar.row].label}: {fmtDate(split.bar.arrival)} → {fmtDate(split.date)} · {split.pitch || "?"}: {fmtDate(split.date)} → {fmtDate(split.bar.departure)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSplit(null)}>{t("cancel")}</Button>
          <Button onClick={doSplit} disabled={busy || !split?.pitch} data-testid="split-confirm">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
